<template>
  <div v-if="show" class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h2>{{ $t('modalSave.saveYourSignature') }}</h2>
        <button class="close-button" @click="$emit('close')">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke="currentColor" class="close-icon"
            fill="none">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="form-container">
        <div v-if="isLoading" class="loader-container">
          <LoaderApp :isLoading="true" />
        </div>

        <div class="link-container" v-else>
          <div v-if="linksGenerated">
            <div class="link-item" v-for="(link, label) in linkDetails" :key="label">
              <label v-if="isExistingUser" class="instruction-label">{{ $t('modalSave.existingUserInstructions')
              }}</label>
              <label v-else>{{ $t('modalSave.linkLabel', { label }) }}</label>

              <div class="link-input-container">
                <template v-if="isExistingUser">
                  <button class="action-button" @click="openLinkInNewTab(link)">
                    {{ $t('modalSave.authorize') }}
                  </button>
                </template>
                <template v-else>
                  <input :value="link" readonly class="link-input" @click="openLinkInNewTab(link)"
                    style="cursor: pointer;" />

                  <div class="icon-container" @mouseenter="showTooltip($t('modalSave.copy'))" @mouseleave="hideTooltip">
                    <button class="copy-button" @click="handleCopyLink(link)">
                      <i class="fas fa-clipboard"></i>
                    </button>
                    <div class="tooltip">{{ tooltip }}</div>
                  </div>

                  <div class="icon-container" @mouseenter="showTooltip($t('modalSave.open'))" @mouseleave="hideTooltip">
                    <button class="open-button" @click="openLinkInNewTab(link)">
                      <i class="fas fa-external-link-alt"></i>
                    </button>
                    <div class="tooltip">{{ tooltip }}</div>
                  </div>
                </template>
              </div>
            </div>

            <p v-if="!extensionUsed" class="info-message">
              {{ $t('modalSave.extensionRecommendPrefix') }}
              <img src="../../assets/images/content/extension.svg" alt="Extension" class="extension-modal" />
              <a href="https://chromewebstore.google.com/detail/gsignature-stopki-e-mail/hgemmbnchnegomnmpngehokoaendhpoe"
                target="_blank" rel="noopener noreferrer">
                {{ $t('modalSave.installExtension') }}
              </a>
              {{ $t('modalSave.extensionRecommendSuffix') }}
            </p>
          </div>

          <div class="email-form" v-else>
            <label class="block-label">
              {{ $t('modalSave.extensionInstallPrefix') }}
              <img src="../../assets/images/content/extension.svg" alt="Extension" class="extension-modal" />
              <a href="https://chromewebstore.google.com/detail/gsignature-stopki-e-mail/hgemmbnchnegomnmpngehokoaendhpoe"
                target="_blank" rel="noopener noreferrer">
                {{ $t('modalSave.installExtension') }}
              </a>
              {{ $t('modalSave.extensionInstallSuffix') }}
            </label>

            <label class="block-label">{{ $t('modalSave.enterEmailInstruction') }}</label>

            <div class="email-input-container">
              <input v-model="emailComputed" type="email" :placeholder="$t('modalSave.enterEmailPlaceholder')"
                class="email-input" @keydown.enter="sendEmail" />
              <button class="action-button" @click="sendEmail">
                {{ $t('modalSave.save') }}
              </button>
            </div>

            <p v-if="emailError" class="error-message">{{ emailError }}</p>

            <p class="disclaimer">
              {{ $t('modalSave.disclaimerText') }}
              <a href="https://www.gsignature.com/terms-of-service" target="_blank">
                {{ $t('modalSave.termsOfService') }}
              </a>.
            </p>
          </div>
        </div>
      </div>

      <div v-if="showToast" class="toast">{{ toastMessage }}</div>
    </div>
  </div>
</template>

<script>
import LoaderApp from "../loader/LoaderView.vue";
import "@fortawesome/fontawesome-free/css/all.css";
import { useRouter } from "vue-router";
import axios from "axios";
import { BACKEND_URL } from "@/environments";

export default {
  name: "ModalSave",
  props: ["show", "formData", "code", "originalFormData"],
  components: { LoaderApp },
  data() {
    return {
      email: "",
      linkName: "",
      originalLinkName: "",
      editLink: "",
      signInLink: "",
      templateUrl: "",
      linkDetails: {},
      linksGenerated: false,
      isLoading: false,
      emailError: "",
      linkNameError: "",
      tooltip: "",
      showToast: false,
      toastMessage: "",
      isExistingUser: false,
      isExtensionUsed: false,
    };
  },
  computed: {
    hasAuthToken() {
      return !!localStorage.getItem("generatorAuthToken");
    },
    decodedToken() {
      const t = localStorage.getItem("generatorAuthToken");
      if (!t) return null;
      try {
        return JSON.parse(atob(t.split(".")[1]));
      } catch {
        return null;
      }
    },
    extensionUsed() {
      return this.isExtensionUsed || localStorage.getItem("extensionUsed") === "true" || this.isExistingUser;
    },
    emailComputed: {
      get() {
        return this.email;
      },
      set(v) {
        this.email = v;
      },
    },
    adminEmail() {
      return this.email;
    },
    domainName() {
      return this.email.includes("@") ? this.email.split("@")[1] : "";
    },
  },
  mounted() {
    if (this.hasAuthToken && this.decodedToken) {
      this.email = this.decodedToken.email;
    }
  },
  setup() {
    const router = useRouter();
    return { basePath: router.options.history.base };
  },
  methods: {
    validateEmail(v) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    },
    getBaseFontSize() {
      switch (this.formData.fontSize) {
        case "small":
          return 12;
        case "large":
          return 16;
        case "medium":
        default:
          return 14;
      }
    },

    async generateUniqueName(email) {
      const { data } = await axios.get(`${BACKEND_URL}/generator/check-link-name`, {
        params: { email },
      });
      if (!data.candidate) throw new Error("Unique name error");
      return data.candidate;
    },
    async registerAccount() {
      const { data } = await axios.post(`${BACKEND_URL}/generator/register`, {
        payload: {
          data: {
            email: this.email,
            language: this.$i18n.locale,
            extensionUsed: this.extensionUsed === "true"
          }
        },
      });
      return data;
    },
    async checkExtension() {
      const { data } = await axios.get(`${BACKEND_URL}/extension/used`, {
        params: { email: this.email }
      });
      return data.extensionUsed;
    },
    async sendEmail() {
      if (!this.validateEmail(this.email)) {
        this.emailError = this.$t("modalSave.invalidEmail");
        return;
      }
      this.emailError = "";
      this.isLoading = true;

      try {
        const reg = await this.registerAccount();
        const prevUsed = localStorage.getItem("extensionUsed") === "true";
        const newUsed = prevUsed || reg.extensionUsed;
        this.isExtensionUsed = newUsed;
        localStorage.setItem("extensionUsed", newUsed ? "true" : "false");
        window.dispatchEvent(new Event("extensionUsedChanged"));
        if (reg.error === "NO_EXTENSION" && !newUsed) {
          this.showToastMessage(this.$t("modalSave.installExtensionFirst"));
          return;
        }
        if (reg.error === "GMAIL") {
          this.showToastMessage(this.$t("modalSave.gmailNotSupported"));
          return;
        }
        if (reg.error === "DOMAIN_NO_USER") {
          this.showToastMessage(this.$t("modalSave.noUserInDomain"));
          return;
        }
        this.signInLink = reg.signInLink || "";

        if (reg.error === "EXISTING_USER") {
          this.linkName = await this.generateUniqueName(this.email);
          this.originalLinkName = this.linkName;

          await axios.post(
            `${BACKEND_URL}/generator/save`,
            {
              settings: this.formData,
              email: this.email,
              linkName: this.linkName,
              originalLinkName: this.originalLinkName,
            }
          );

          this.linkDetails = { Login: "https://app.gsignature.com/generator" };
          this.linksGenerated = true;
          this.isExistingUser = true;
          return;
        }

        await this.prepareLinksAndTemplate();
        this.buildLinkDetailsNew();
        this.showToastMessage(this.$t("modalSave.accountCreated"));
      } catch (e) {
        console.error("sendEmail flow error:", e);
        this.showToastMessage(this.$t("modalSave.genericError"));
      } finally {
        this.isLoading = false;
      }
    },

    async prepareLinksAndTemplate() {
      this.linkName = await this.generateUniqueName(this.email);
      this.originalLinkName = this.linkName;

      const { data: saveGen } = await axios.post(`${BACKEND_URL}/generator/save`, {
        settings: this.formData,
        email: this.email,
        linkName: this.linkName,
        originalLinkName: this.originalLinkName,
      });

      if (saveGen.error) {
        this.linkNameError = saveGen.error;
        throw new Error(saveGen.error);
      }

      const domain = window.location.origin;
      const base = this.basePath.endsWith("/") ? this.basePath : `${this.basePath}/`;
      this.editLink = `${domain}${base}settings/${saveGen.id}`;

      await this.saveTemplate(saveGen);
    },

    async saveTemplate(saveGen) {
      const sig = this.$parent.$refs.signaturePreview;
      if (!sig) throw new Error("Signature preview not found");

      const clone = sig.$el.cloneNode(true);

      const p = this.formData;
      if (p.banner?.url) {
        const imgs = clone.querySelectorAll('img');
        imgs.forEach(img => {
          if (img.src.includes(p.banner.url)) {
            const wrapper = img.closest('table') || img.closest('div') || img;
            wrapper.remove();
          }
        });
      }

      if (p.disclaimer) {
        const walker = document.createTreeWalker(clone, NodeFilter.SHOW_ELEMENT, null, false);
        let node;
        while ((node = walker.nextNode())) {
          if (node.textContent.includes(p.disclaimer)) {
            node.remove();
          }
        }
      }

      const rawHtmlClean = clone.innerHTML;
      const processed = typeof this.$parent.processHtml === "function"
        ? this.$parent.processHtml(rawHtmlClean)
        : rawHtmlClean;
      const signatureCode = String(processed || "")
        .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "")
        .replace(/<\/?(?:iframe|object|embed|link|meta|base|form)\b[^>]*>/gi, "")
        .replace(/\s+on[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "")
        .replace(/\s(?:href|src|xlink:href)\s*=\s*(["'])\s*javascript:[^"']*\1/gi, "");

      const bannerAddon = p.banner?.url
        ? {
          imageUrl: p.banner.url,
          imageWidth: p.bannerWidth,
          name: 'Generator Banner'
        }
        : null;
      const safeDisclaimer = String(p.disclaimer || "")
        .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "")
        .replace(/<\/?(?:iframe|object|embed|link|meta|base|form)\b[^>]*>/gi, "")
        .replace(/\s+on[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "")
        .replace(/\s(?:href|src|xlink:href)\s*=\s*(["'])\s*javascript:[^"']*\1/gi, "");
      const disclaimerAddon = safeDisclaimer
        ? {
          name: 'Generator Disclaimer',
          htmlContent: `
            <table cellspacing="0" cellpadding="0" border="0" width="${p.disclaimerWidth ? '100%' : p.signatureWidth}">
            <tr>
                <td style="padding-top: 3px">
                <p style="
                    font-family: ${p.fontFamily};
                    color: ${p.addonsColor};
                    font-size: ${this.getBaseFontSize() - 2}px;
                ">
                    ${safeDisclaimer}
                </p>
                </td>
            </tr>
            </table>`
        }
        : null;

      const authToken = localStorage.getItem('generatorAuthToken');
      const authHeaders = authToken
        ? { Authorization: `Bearer ${authToken}` }
        : {};

      const { data: gen } = await axios.get(`${BACKEND_URL}/generator/check-link-name`, { params: { email: this.adminEmail } });
      const templateName = gen.candidate;

      await axios.post(
        `${BACKEND_URL}/generator/check-or-create-domain`,
        { domain: this.domainName },
        { headers: authHeaders }
      );

      const { data: saveResp } = await axios.post(
        `${BACKEND_URL}/generator/save-signature`,
        {
          domain: this.domainName,
          name: templateName,
          signatureCode,
          phoneLabel: "",
          mobileLabel: "",
          emailLabel: "",
          banner: bannerAddon,
          disclaimer: disclaimerAddon,
          generatorLink: saveGen.id
        },
        { headers: authHeaders }
      );
      if (!saveResp.templateId) throw new Error("Template save failed");
      this.templateUrl = `https://app.gsignature.com/signatures/custom/${saveResp.templateId}`;
    },
    buildLinkDetailsNew() {
      if (!this.signInLink) {
        this.signInLink = "https://app.gsignature.com";
      }
      this.linkDetails = { Login: this.signInLink };
      this.linksGenerated = true;
    },
    openLinkInNewTab(l) {
      if (l) window.open(l, "_blank");
    },
    handleCopyLink(l) {
      navigator.clipboard.writeText(l).then(() => {
        this.showToastMessage(this.$t("modalSave.copied"));
      });
    },
    showTooltip(t) {
      this.tooltip = t;
    },
    hideTooltip() {
      this.tooltip = "";
    },
    showToastMessage(m) {
      this.toastMessage = m;
      this.showToast = true;
      setTimeout(() => (this.showToast = false), 3000);
    },

    resetModal() {
      Object.assign(this.$data, {
        email: this.hasAuthToken && this.decodedToken ? this.decodedToken.email : "",
        linkName: "",
        originalLinkName: "",
        editLink: "",
        signInLink: "",
        templateUrl: "",
        linkDetails: {},
        linksGenerated: false,
        emailError: "",
        linkNameError: "",
        isLoading: false,
        tooltip: "",
        showToast: false,
        toastMessage: "",
        isExistingUser: false,
        isExtensionUsed: false,
      });
    },
  },

  watch: {
    show(v) {
      if (!v) {
        this.$emit("restore-original-form-data");
        this.resetModal();
      } else {
        const stored = sessionStorage.getItem("brandEmail");
        if (stored && !this.email) this.email = stored;
      }
    },
  },
};
</script>

<style scoped>
.modal-content {
  background: linear-gradient(135deg, #ffffff, #e0e0e0);
  border-radius: 20px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
}

.instruction-label {
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
  display: block;
}

.form-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
}

.link-container {
  width: 100%;
  box-sizing: border-box;
}

.block-label {
  display: block;
  margin-bottom: 0.5rem;

  img {
    margin-right: 0.2rem;
  }
}

.info-message {
  display: inline-block;
  width: 100%;
  box-sizing: border-box;
  white-space: normal;
  text-align: left;
  background-color: #e2e2e2;
  border-left: 4px solid #ff6a00;
  padding: 10px 15px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #333;
}

.info-message img,
.info-message a {
  vertical-align: middle;
  margin: 0 0.4rem;
}

.extension-modal {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.info-message a {
  font-weight: 600;
  text-decoration: underline;
}

.email-input-container {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 20px;
}

.email-input,
.link-input {
  border: 2px solid #ccc;
  border-radius: 8px;
  font-size: 16px;
  padding: 12px;
  flex: 1;
}

.email-input {
  margin-right: 10px;
}

.link-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 20px;
  width: 100%;
}

.link-input-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.copy-button,
.open-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
  font-size: 20px;
  transition: transform 0.3s ease;
}

.copy-button:hover,
.open-button:hover {
  transform: scale(1.2);
}

.icon-container {
  position: relative;
  display: inline-block;
}

.tooltip {
  position: absolute;
  bottom: 100%;
  left: 90%;
  transform: translateX(-50%);
  padding: 5px 10px;
  background: #333;
  color: #fff;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
}

.icon-container:hover .tooltip {
  opacity: 1;
  visibility: visible;
}

.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.toast {
  position: fixed;
  bottom: 25px;
  left: 50%;
  transform: translateX(-50%);
  background: #555;
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  z-index: 1000;
}

.error-message {
  color: red;
  font-size: 14px;
  margin-top: 5px;
}

.disclaimer {
  font-size: 12px;
  color: #666;
  margin-top: 15px;
  text-align: center;
}

.disclaimer a {
  color: #007bff;
  text-decoration: underline;
}

.dark-mode .modal-content {
  background: linear-gradient(135deg, #2e2e2e, #4a4a4a);
  color: #ddd;
}

.dark-mode .email-input,
.dark-mode .link-input {
  background: #333;
  border-color: #555;
  color: #ddd;
}

.dark-mode .copy-button i,
.dark-mode .open-button i {
  color: #bbb;
}
</style>
